/**
 * One session's journey — what the row in Every session did, step by step.
 *
 * The row's facts at the top (when, where it came from, who), then its acts in
 * order: the published-link follow it landed from, if any, and every page,
 * simulation, lab and answer after it, with the time since landing and how
 * long each lasted. Past the events' 14 days the steps come from the session's
 * summary, which keeps what and how often but not when — and says so.
 */
import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { withReturnTo } from '@shared-ui/navigation';
import { Modal } from '@shared-ui/components';
import { sessionsApi, type JourneyStep, type LandingSession } from '../../../api/siteAnalyticsApi';

/** What an act kind reads as. The lab is a page view of `/lab`. */
function actLabel(s: JourneyStep): string {
  if (s.kind === 'page_view') return s.where === '/lab' ? 'Opened the lab' : 'Viewed page';
  // A practice session's question screen, recorded with which question it was.
  if (s.kind === 'screen_view' && typeof s.props?.questionId === 'string') {
    const pos = typeof s.props.position === 'number' ? ` ${s.props.position}` : '';
    return `${/#review/.test(s.where ?? '') ? 'Reviewed' : 'Solving'} question${pos}`;
  }
  const labels: Record<string, string> = {
    screen_view: 'Screen', click: 'Clicked', simulation_open: 'Opened simulation',
    link_open: 'Followed link', answer_submitted: 'Answered a question', hint_request: 'Asked for a hint',
    solution_analysis_view: 'Viewed the solution', concept_lab_open: 'Opened the concept lab',
    chemistry_lab_session: 'Ran the chemistry lab', chemistry_lab_mix: 'Mixed in the chemistry lab',
    text_chat_start: 'Started a text chat', app_open: 'Opened the app',
  };
  return labels[s.kind] ?? s.kind.replace(/_/g, ' ');
}

/** `/simulations/x#youngs-double-slit` → the page, and the thing on it. */
function whereOf(where?: string): { page?: string; thing?: string } {
  if (!where) return {};
  const [page, thing] = where.split('#');
  return { page, ...(thing ? { thing } : {}) };
}

const OS_NAMES: Record<string, string> = {
  ios: 'iOS', android: 'Android', windows: 'Windows', macos: 'macOS', chromeos: 'ChromeOS', linux: 'Linux', other: 'other OS',
};
const viaLabel = (v: string) => ({ google: 'Google', apple: 'Apple', email: 'email', phone: 'phone' } as Record<string, string>)[v] ?? v;

const clock = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const dwell = (ms?: number) => (ms === undefined ? '' : ms < 1000 ? '<1s' : ms < 60_000 ? `${Math.round(ms / 1000)}s`
  : `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`);
const when = (iso: string) => new Date(iso).toLocaleString(undefined, {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
});

/** A question the journey names — its text as a link that opens it over the
 *  Questions list, the way the rest of the console links to a question. */
function QuestionLink({ id, q, onClose }: { id: string; q?: { text: string; subject?: string }; onClose: () => void }) {
  return (
    <Link to={`/questions?id=${encodeURIComponent(id)}`} {...withReturnTo({ label: 'Site usage' })} onClick={onClose}
      className="sj-question" title={q ? `${q.subject ? `${q.subject} · ` : ''}${id}` : id}>
      {q?.text || id}
    </Link>
  );
}

export default function SessionJourneyDialog({ session, onClose }: { session: LandingSession | null; onClose: () => void }) {
  const journey = useQuery({
    queryKey: ['session-journey', session?.sessionId, session?.site, session?.userId],
    queryFn: () => sessionsApi.journey(session!),
    enabled: !!session,
  });
  if (!session) return null;
  const j = journey.data;
  const start = Date.parse(j?.startedAt ?? session.landedAt);
  const ft = session.firstTouch;
  // Where the sign-up falls among the steps: before step i, after the last, or
  // not in this session at all (-1). Only a timed journey can place it.
  const signedUpAt = j?.account?.signedUpAt;
  const lastAt = Date.parse(j?.lastActivityAt ?? session.landedAt);
  const signUpStep = !j || j.fromSummary || !signedUpAt
    || Date.parse(signedUpAt) < start || Date.parse(signedUpAt) > lastAt + 5 * 60_000
    ? -1
    : (() => { const k = j.steps.findIndex(s => s.t && s.t > signedUpAt); return k < 0 ? j.steps.length : k; })();

  return (
    <Modal isOpen onClose={onClose} title="Session journey" size="lg">
      <dl className="sj-facts">
        <dt>Landed</dt><dd>{when(session.landedAt)} · {session.site === 'app' ? 'BuzzBrain app' : 'Science site'} · {session.landing}</dd>
        <dt>Came from</dt>
        <dd>
          {session.viaLink ? <>published link <code>{session.viaLink.slug}</code>{session.viaLink.placement && ` (${session.viaLink.placement})`}</> : 'no published link'}
          {' · referrer '}{session.referrerHost ?? 'none'}
        </dd>
        <dt>Who</dt>
        <dd>
          {session.userId
            ? <Link to={`/users/${encodeURIComponent(session.userId)}`} {...withReturnTo({ label: 'Site usage' })} onClick={onClose}>{session.userLabel ?? `${session.userId.slice(0, 8)}…`}</Link>
            : 'no account'}
          {' · '}{session.account === 'signed-in' ? 'signed in' : session.account === 'guest' ? 'guest' : 'no account'}
          {session.returning && ` · ${session.returning}`}
          {/* For someone coming back: how long they have been around, and how
              many times before this. */}
          {session.returning === 'returning' && j?.account && (
            <>
              {j.account.createdAt && ` · account made ${when(j.account.createdAt)}`}
              {` · ${j.account.priorIsFloor ? 'at least ' : ''}${j.account.priorSessions.toLocaleString()} `
                + `session${j.account.priorSessions === 1 ? '' : 's'} before this one`}
              {j.account.priorIsFloor && <span className="sa-zero" title="Sessions are kept 180 days"> (last 180 days)</span>}
            </>
          )}
          {j?.account?.signedUpAt && ` · signed up ${when(j.account.signedUpAt)}${j.account.signedUpVia ? ` with ${viaLabel(j.account.signedUpVia)}` : ''}`}
          {session.internal && ' · internal'}
          {session.device && ` · ${session.device}`}
          {session.os && ` · ${OS_NAMES[session.os] ?? session.os}`}
          {session.inApp && ` · in the ${session.inApp} app`}
        </dd>
        {ft && (
          <>
            <dt>First touch</dt>
            <dd>{[ft.source, ft.medium, ft.campaign, ft.variant].filter(Boolean).join(' · ') || '—'}</dd>
          </>
        )}
      </dl>

      {journey.isLoading && <p className="sa-empty">Loading…</p>}
      {journey.error && <p className="sa-error">{(journey.error as Error).message}</p>}
      {j && (
        <>
          {j.fromSummary && (
            <p className="sa-note">
              This session is older than the 14 days its events are kept, so its steps come from its summary: what it
              did and how often, in the order it first did each — but not the minute of each.
            </p>
          )}
          {j.steps.length === 0 && !j.arrival
            ? <p className="sa-empty">Nothing was recorded for this session.</p>
            : (
              <ol className="sj-steps">
                {j.arrival && (
                  <li className="sj-step is-arrival">
                    <span className="sj-at">{clock(Date.parse(j.arrival.at) - start)}</span>
                    <span className="sj-act">Followed link</span>
                    <span className="sj-where">
                      <code>{j.arrival.slug ?? '?'}</code>
                      {j.arrival.placement && <span className="sj-thing">{j.arrival.placement}</span>}
                      {j.arrival.campaign && <span className="sj-thing">campaign {j.arrival.campaign}</span>}
                    </span>
                    <span className="sj-dwell" />
                  </li>
                )}
                {j.steps.map((s, i) => {
                  const { page, thing } = whereOf(s.where);
                  // The sign-up, at the moment it happened, if it fell in this session.
                  const signUpHere = signUpStep === i;
                  return (<Fragment key={i}>
                    {signUpHere && (
                      <li className="sj-step is-signup">
                        <span className="sj-at">{clock(Date.parse(j.account!.signedUpAt!) - start)}</span>
                        <span className="sj-act">Signed up</span>
                        <span className="sj-where">{j.account!.signedUpVia && <span className="sj-thing">with {viaLabel(j.account!.signedUpVia)}</span>}</span>
                        <span className="sj-dwell" />
                      </li>
                    )}
                    <li className="sj-step">
                      <span className="sj-at">{s.t ? clock(Date.parse(s.t) - start) : '—'}</span>
                      <span className="sj-act">{actLabel(s)}{s.n && s.n > 1 ? ` ×${s.n}` : ''}</span>
                      <span className="sj-where">
                        {typeof s.props?.questionId === 'string'
                          ? <QuestionLink id={s.props.questionId} q={j.questions?.[s.props.questionId]} onClose={onClose} />
                          : (
                            <>
                              {page && <span className="sj-page">{page}</span>}
                              {thing && <span className="sj-thing">{thing}</span>}
                            </>
                          )}
                        {s.entities && Object.entries(s.entities).map(([k, n]) => (j.questions?.[k]
                          ? <span key={k}><QuestionLink id={k} q={j.questions[k]} onClose={onClose} />{n > 1 ? ` ×${n}` : ''}</span>
                          : <span key={k} className="sj-thing">{k}{n > 1 ? ` ×${n}` : ''}</span>
                        ))}
                      </span>
                      <span className="sj-dwell">{dwell(s.dwellMs)}</span>
                    </li>
                  </Fragment>);
                })}
                {signUpStep === j.steps.length && (
                  <li className="sj-step is-signup">
                    <span className="sj-at">{clock(Date.parse(j.account!.signedUpAt!) - start)}</span>
                    <span className="sj-act">Signed up</span>
                    <span className="sj-where">{j.account!.signedUpVia && <span className="sj-thing">with {viaLabel(j.account!.signedUpVia)}</span>}</span>
                    <span className="sj-dwell" />
                  </li>
                )}
              </ol>
            )}
        </>
      )}
    </Modal>
  );
}
